import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Play, ShoppingCart, X, Check, ArrowRight, Video } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Videos tab for the public Gallery page. Mirrors the photo cart/checkout flow.
 * - Guest checkout supported
 * - Preview playback in modal (watch for free, must pay to download)
 * - Video price fetched from /api/media-pricing (sub vs non-sub aware)
 */
export const GalleryVideosTab = ({ pricing, filterEventId }) => {
  const { user } = useAuth();
  const { eventId: urlEventId } = useParams();
  const effectiveEventId = filterEventId || urlEventId || null;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]); // array of {video, event_title}
  const [cartOpen, setCartOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [checkoutEmail, setCheckoutEmail] = useState(user?.email || '');
  const [checkoutName, setCheckoutName] = useState(user?.name || '');
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/gallery/videos`);
        if (cancelled) return;
        let evs = res.data.events || [];
        if (effectiveEventId) {
          evs = evs.filter(e => e.event_id === effectiveEventId);
        }
        setEvents(evs);
      } catch (err) {
        console.error('Failed to load gallery videos:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [effectiveEventId]);

  const perVideoPrice = pricing?.video_gallery_price != null
    ? Number(pricing.video_gallery_price)
    : 20.0;
  const perVideo = perVideoPrice.toFixed(2);
  const cartTotal = (cart.length * perVideoPrice).toFixed(2);
  const inCart = (id) => cart.some(v => v.id === id);
  const toggleCart = (video) => {
    setCart(prev => inCart(video.id) ? prev.filter(v => v.id !== video.id) : [...prev, video]);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    if (!checkoutEmail || !checkoutEmail.includes('@')) { toast.error('Please provide a valid email'); return; }
    setCheckingOut(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API_URL}/api/videos/cart/checkout`, {
        video_ids: cart.map(v => v.id),
        customer_email: checkoutEmail,
        customer_name: checkoutName,
        origin_url: window.location.origin,
      }, { headers });
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Video checkout failed:', err);
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  return (
    <div>
      {events.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <Video className="w-16 h-16 mx-auto mb-4 opacity-40" />
          <p>No videos available yet.</p>
        </div>
      ) : (
        <div className="space-y-12">
          {events.map(ev => (
            <div key={ev.event_id} data-testid={`gallery-video-event-${ev.event_id}`}>
              <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h2 className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tight">{ev.event_title}</h2>
                  <p className="text-sm text-muted-foreground">{ev.total_videos} video{ev.total_videos !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {(ev.videos || []).map(video => {
                  const selected = inCart(video.id);
                  const thumb = video.thumbnail_url;
                  const buyable = !!video.for_sale;
                  return (
                    <div
                      key={video.id}
                      className={`relative aspect-video rounded-lg overflow-hidden group cursor-pointer transition-all bg-muted ${selected ? 'ring-4 ring-primary' : 'ring-1 ring-border'}`}
                      data-testid={`gallery-video-${video.id}`}
                    >
                      <div className="absolute inset-0" onClick={() => setPreviewVideo({ ...video, event_title: ev.event_title })}>
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={video.filename}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            loading="lazy"
                            draggable={false}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <Video className="w-10 h-10 opacity-40" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                          <div className="bg-white/95 rounded-full p-3 shadow-lg group-hover:scale-110 transition-transform">
                            <Play className="w-6 h-6 text-primary fill-primary" />
                          </div>
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pointer-events-none">
                        <p className="text-white text-xs font-medium truncate">{video.filename}</p>
                      </div>
                      {buyable ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleCart({ ...video, event_title: ev.event_title }); }}
                          className={`absolute top-2 right-2 z-30 rounded-full p-2 shadow-lg transition-colors ${selected ? 'bg-primary text-primary-foreground' : 'bg-white/90 text-foreground hover:bg-white'}`}
                          data-testid={`gallery-video-cart-toggle-${video.id}`}
                          title={selected ? 'Remove from cart' : `Add to cart · $${perVideo}`}
                        >
                          {selected ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
                        </button>
                      ) : (
                        <span
                          className="absolute top-2 right-2 z-30 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-black/70 text-white shadow-lg pointer-events-none"
                          data-testid={`gallery-video-watch-only-${video.id}`}
                          title="Watch only — not available for purchase"
                        >
                          Watch only
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Floating cart button */}
      {cart.length > 0 && (
        <button
          onClick={() => setCartOpen(true)}
          className="fixed bottom-24 right-6 z-40 bg-primary text-primary-foreground rounded-full px-6 py-4 shadow-2xl flex items-center gap-3 hover:scale-105 transition-transform"
          data-testid="open-video-cart-btn"
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="font-bold">{cart.length} video{cart.length !== 1 ? 's' : ''} · ${cartTotal}</span>
        </button>
      )}

      {/* Cart sidebar */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)} />
          <div className="relative w-full max-w-md bg-background h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-background border-b border-border p-4 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl uppercase">Your Video Cart</h2>
              <button onClick={() => setCartOpen(false)} className="p-2 hover:bg-muted rounded-full" data-testid="close-video-cart-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {cart.map(v => (
                <div key={v.id} className="flex items-center gap-3 p-2 border border-border rounded-lg">
                  <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0 relative">
                    {v.thumbnail_url ? (
                      <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" draggable={false} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-6 h-6 opacity-40" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{v.event_title || 'Event Video'}</p>
                    <p className="text-xs text-muted-foreground truncate">{v.filename}</p>
                  </div>
                  <button onClick={() => toggleCart(v)} className="text-muted-foreground hover:text-destructive p-1" title="Remove">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {pricing && (
                <>
                  <div className="border-t border-border pt-4 mt-4 space-y-3">
                    <div>
                      <Label htmlFor="video-checkout-name">Your Name</Label>
                      <Input
                        id="video-checkout-name"
                        value={checkoutName}
                        onChange={(e) => setCheckoutName(e.target.value)}
                        placeholder="Full name"
                        data-testid="video-checkout-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="video-checkout-email">Email *</Label>
                      <Input
                        id="video-checkout-email"
                        type="email"
                        value={checkoutEmail}
                        onChange={(e) => setCheckoutEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        data-testid="video-checkout-email-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Download links delivered immediately after payment.</p>
                    </div>
                  </div>

                  <div className="border-t border-border pt-4 mt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{cart.length} video{cart.length !== 1 ? 's' : ''} × ${perVideo}</span>
                      <span className="font-semibold">${cartTotal}</span>
                    </div>
                    {pricing && !pricing.is_subscriber && !pricing.is_admin_or_coach
                      && pricing.pricing?.video_gallery_price_sub != null
                      && Number(pricing.pricing.video_gallery_price_sub) < Number(pricing.pricing.video_gallery_price_nonsub) && (
                      <p className="text-xs text-primary bg-primary/10 rounded p-2">
                        💡 Subscribers pay ${Number(pricing.pricing.video_gallery_price_sub).toFixed(2)}/video. <Link to="/signup" className="underline font-bold">Join now</Link>
                      </p>
                    )}
                    <Button
                      onClick={handleCheckout}
                      disabled={checkingOut || cart.length === 0}
                      className="w-full font-bold uppercase tracking-wider"
                      size="lg"
                      data-testid="video-checkout-btn"
                    >
                      {checkingOut ? 'Redirecting…' : `Pay $${cartTotal}`}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewVideo && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setPreviewVideo(null)}>
          <button
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full z-10"
            onClick={() => setPreviewVideo(null)}
            data-testid="close-video-preview"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <video
              src={previewVideo.url}
              controls
              autoPlay
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
              className="w-full max-h-[80vh] rounded-lg"
            />
            <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-white font-medium">{previewVideo.event_title}</p>
                <p className="text-white/60 text-sm truncate">{previewVideo.filename}</p>
              </div>
              <Button
                onClick={(e) => { e.stopPropagation(); toggleCart(previewVideo); }}
                variant={inCart(previewVideo.id) ? 'secondary' : 'default'}
                data-testid="video-preview-cart-btn"
              >
                {inCart(previewVideo.id) ? (
                  <><Check className="w-4 h-4 mr-2" /> In Cart</>
                ) : (
                  <><ShoppingCart className="w-4 h-4 mr-2" /> Add to Cart · ${perVideo}</>
                )}
              </Button>
            </div>
            <p className="text-white/50 text-xs mt-2 italic text-center">Preview only — full download available after purchase.</p>
          </div>
        </div>
      )}

      {/* CTA under grid */}
      {!effectiveEventId && (
        <div className="mt-12 bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30 rounded-2xl p-6 md:p-8 text-center">
          <h3 className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tight">Need a Custom-Edited Run?</h3>
          <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
            We can pull specific run footage, edit it, and deliver a polished clip. Great for social posts and sponsors.
          </p>
          <Link to="/events">
            <Button size="lg" className="mt-5 rounded-full px-8 font-bold uppercase tracking-wider" data-testid="order-custom-video-btn">
              Order a Custom Video <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default GalleryVideosTab;
